import { IFormTab, IFormRow } from '../model/form.model';
import { get, omit } from 'lodash';
import { IExtendedFormlyFieldConfig } from '../model/form.types';
import { createExpressionProperties } from './conditional-fields-expression-properties';
import { createFieldsMap, FieldTipFieldMap } from './create-fields-map';
import { IField } from '../model/field';
import { defaultLang } from 'src/app/shared/constants';
import { SECTION_HEADER_FORM_CONFIG } from '../model/form-options';
import { Subject } from 'rxjs';
import { language } from '../contact-self-service-forms/contact-self-service-forms.service';
import { FormlyFieldConfig } from '@ngx-formly/core';

export interface IFormTabRendering {
  tabName: string;
  fields: IExtendedFormlyFieldConfig[];
}

interface IRenderParams {
  tabs: IFormTab[];
  typeFields?: IField[];
}

export function prepareFormForRendering({ tabs, typeFields }: IRenderParams): IFormTabRendering[] {
  try {
    const typeFieldsMap = createFieldsMap(typeFields);
    return tabs.map(({ tabName, rows }) => (
      {
        tabName,
        fields: reduceRows({ rows, typeFieldsMap })
      }
    ));
  } catch (e) {
    throw new Error(`
      The prepareFormForRendering function failed. Source:

      ---------------
      ${e}
      ---------------

      Form tabs config passed in: ${JSON.stringify(tabs, null, 2)}
    `);
  }
}


interface IReduceRowsParams {
  rows: IFormRow[];
  typeFieldsMap: FieldTipFieldMap;
}

export function reduceRows({ rows, typeFieldsMap }: IReduceRowsParams): IExtendedFormlyFieldConfig[] {
  const reducedRows = rows.reduce((acc, row) => {
    const rowFieldGroup = createRowFieldGroup({ fields: row.fields, typeFieldsMap });

    if (rowFieldGroup.fieldGroup.length === 0) {
      // field group will be empty if all fields are invalid
      return acc;
    }
    const sectionHeaderToggle = containsSectionHeader(row?.fields ?? []);
    const sectionTitle = getTitleSectionHeader(row?.fields ?? []);
    const defaultBehaviour = getDefaultBehaviourSectionHeader(row?.fields ?? []);
    const conditionalFields = getConditionalFieldsSectionHeader(rowFieldGroup?.fieldGroup ?? []);
    if (sectionHeaderToggle) {
      acc.sectionIndex = acc.rows.length;
      acc.rows.push(createSectionFieldGroup(sectionTitle, defaultBehaviour, ...conditionalFields));
    }

    if (acc.sectionIndex !== -1) {
      acc.rows[acc.sectionIndex].fieldGroup.push(rowFieldGroup);
    } else {
      acc.rows.push(rowFieldGroup);
    }
    return acc;

  }, { rows: [], sectionIndex: -1 });

  return reducedRows.rows;
}

export function containsSectionHeader(fields: IExtendedFormlyFieldConfig[]): Subject<boolean> | boolean {
  const section = field => field.type === SECTION_HEADER_FORM_CONFIG.type;
  const sectionHeader = fields.find(section);

  if (sectionHeader) {
    const toggle: Subject<boolean> = new Subject();

    if (!sectionHeader.templateOptions) {
      sectionHeader.templateOptions = {};
    }

    sectionHeader.templateOptions.toggle = toggle;

    return toggle;
  }

  return false;
}

export function getTitleSectionHeader(fields: IExtendedFormlyFieldConfig[]): string {
  const section = field => field.type === SECTION_HEADER_FORM_CONFIG.type;
  const sectionHeader = fields.find(section);

  /*
  @language is fetched from service
  sometimes using navigator is not fetching the language needed for chinese language
  */
  if (sectionHeader) {
    return get(sectionHeader, `templateOptions.i18n_title.${language}`, get(sectionHeader, `templateOptions.i18n_title.${defaultLang}`, ''));
  }

  return '';
}

export function getDefaultBehaviourSectionHeader(fields: IExtendedFormlyFieldConfig[]): string {
  const section = field => field.type === SECTION_HEADER_FORM_CONFIG.type;
  const sectionHeader = fields.find(section);

  if (sectionHeader) {
    return get(sectionHeader, 'templateOptions.defaultBehaviour', '');
  }

  return '';
}

export function getConditionalFieldsSectionHeader(fields: IExtendedFormlyFieldConfig[]): [IExtendedFormlyFieldConfig["conditionalFields"], boolean, (model: any, formState: any, field?: FormlyFieldConfig) => boolean ]  {
  const section = field => field.type === SECTION_HEADER_FORM_CONFIG.type;
  const sectionHeader = fields.find(section);

  return [get(sectionHeader, 'conditionalFields', {}), get(sectionHeader, 'isConditional', false), get(sectionHeader, 'hideExpression', null)];
}

export function createSectionFieldGroup(title: string, defaultBehaviour?: string, conditionalFields?: IExtendedFormlyFieldConfig['conditionalFields'], isConditional?: boolean, hideExpression?: any) {
  return {
    wrappers: ['noggin-section'],
    fieldGroup: [],
    templateOptions: {
      title,
      defaultBehaviour,
      conditionalFields,
    },
    hideExpression,
    isConditional,
  };
}

interface ICreateRowFieldGroupParams {
  fields: IExtendedFormlyFieldConfig[];
  typeFieldsMap: FieldTipFieldMap;
}

export function createRowFieldGroup({ fields, typeFieldsMap }: ICreateRowFieldGroupParams) {
  const fieldGroupClassName = requiresHeaderRowClass(fields[0])
  ? 'noggin-form-row noggin-form-section-header-row'
  : 'noggin-form-row';
  return {
    fieldGroupClassName,
    fieldGroup: fields.reduce(createTransformFieldReducer(typeFieldsMap), [])
  };
}

export function createTransformFieldReducer(typeFieldsMap: FieldTipFieldMap) {
  return function transformFieldReducer(acc: IExtendedFormlyFieldConfig[], field: IExtendedFormlyFieldConfig):
    IExtendedFormlyFieldConfig[] {
    const fieldTip = getContextTypeTip(field);
    const validFieldTip = validateFieldTip(fieldTip, typeFieldsMap);
    let fieldValue: IField | undefined;

    if (!validFieldTip) {
      return acc;
    } else {
      fieldValue = typeFieldsMap.get(fieldTip);
    }

    const createdExpressions = createExpressionProperties(field.conditionalFields, fieldValue);
    const expressionProperties = omit(createdExpressions, 'templateOptions.visibility');
    const visibilityExpression = get(createdExpressions, 'templateOptions.visibility', null);

    const update = {
      ...field,
      key: fieldTip,
      expressionProperties,
      modelOptions: {
        updateOn: 'submit'
      }
    } as IExtendedFormlyFieldConfig;

    update.isConditional = get(field.conditionalFields, ['visibility', 'value']) === 'conditional';

    if (visibilityExpression) {
      update.hideExpression = (...args) => !visibilityExpression(...args);
    }

    /*
    @language is fetched from service
    sometimes using navigator is not fetching the language needed for chinese language
    */
    update.templateOptions.placeholder = get(update, `templateOptions.i18n_placeholder.${language}`, get(update, `templateOptions.i18n_placeholder.${defaultLang}`, ''));
    update.templateOptions.label = get(update, `templateOptions.i18n_label.${language}`, get(update, `templateOptions.i18n_label.${defaultLang}`, ''));

    if (fieldValue && fieldValue.minLength) {
      update.templateOptions.minLength = fieldValue.minLength;
    }

    if (fieldValue && fieldValue.maxLength) {
      update.templateOptions.maxLength = fieldValue.maxLength;
    }

    if (fieldValue && fieldValue.minimum) {
      update.templateOptions.min = fieldValue.minimum;
    }

    if (fieldValue && fieldValue.maximum) {
      update.templateOptions.max = fieldValue.maximum;
    }

    return [...acc, update];
  };
}

function validateFieldTip(tip: string | null, typeFieldsMap: FieldTipFieldMap) {
  if (tip === null) {
    return true;
  }
  if (typeFieldsMap.size === 0) {
    return true;
  }
  return typeFieldsMap.has(tip);
}

function getContextTypeTip(field: IExtendedFormlyFieldConfig): string | null {
  // section headers and other elements may not have tips
  const contextTypeTip = field.templateOptions.contextTypeTip;
  if (typeof contextTypeTip !== 'undefined' && contextTypeTip.length < 1) {
    // context type tips should have a length if they exist
    const errorMessage = `
      Context type tip missing on field:

        ${JSON.stringify(field, null, 2)}
    `;
    return 'invalid-tip';
  }

  return contextTypeTip || null;
}

function isSectionHeader(field: IExtendedFormlyFieldConfig): boolean {
  return field && field.type === 'section-header';
}

function hasSectionWrappper(field: IExtendedFormlyFieldConfig): boolean {
  return field
    && field.wrappers
    && field.wrappers.includes('side-sheet-section-wrapper');
}

function requiresHeaderRowClass(field: IExtendedFormlyFieldConfig): boolean {
  return isSectionHeader(field) || hasSectionWrappper(field);
}
